import {
  CheckboxField,
  AlertIcon,
  Flex,
  Image,
  Popover,
  SortUpIcon,
  Typography,
} from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import type { RowData } from '@tanstack/react-table';
import { legacyCreateColumnHelper } from '@tanstack/react-table/legacy';
import type {
  LegacyColumn,
  LegacyColumnDef,
} from '@tanstack/react-table/legacy';
import capitalize from 'lodash-es/capitalize';
import { useMemo } from 'react';

import { labelForVal } from '@jane/shared/util';

import { lineageLabel } from './lineageLabel';
import { priceWeightLabel } from './priceWeightLabel';
import type { Filters, Product, ProductFilters } from './productSelect.types';
import { INVENTORY_FILTER_VALUES } from './productSelect.util';

const SortableHeader = <R extends RowData, TValue>({
  column,
  label,
}: {
  column: LegacyColumn<R, TValue>;
  label: string;
}) => {
  const sortByAsc = column.getIsSorted() === 'asc';
  const isSorted = sortByAsc || column.getIsSorted() === 'desc';
  const toggleSort = () => column.toggleSorting(sortByAsc);

  return (
    <Flex
      alignItems="center"
      onClick={toggleSort}
      aria-label={`Sort by ${sortByAsc ? 'ascending' : 'descending'}`}
      width="100%"
    >
      <Typography color="grays-mid" variant="caps-regular" truncateAt="100%">
        {label}
      </Typography>
      <div>
        <SortUpIcon
          color={isSorted ? 'primary-dark' : 'primary-light'}
          rotate={sortByAsc ? 'up' : 'down'}
        />
      </div>
    </Flex>
  );
};

/**
 * The shared product table's column set. `R` is the consumer's own row type, so
 * any row carrying the fields these columns read works without a cast.
 */
export const useProductSelectColumns = <R extends Partial<Product>>({
  filters,
  productCount,
  selectable = true,
  unfilterable,
}: {
  filters: Filters;
  productCount: number;
  selectable?: boolean;
  /** Columns to render without a filter, for a catalog that can't serve one */
  unfilterable?: ProductFilters[];
}): LegacyColumnDef<R>[] => {
  const columnHelper = useMemo(() => legacyCreateColumnHelper<R>(), []);

  return useMemo<LegacyColumnDef<R>[]>(() => {
    const selectColumn = columnHelper.display({
      id: 'select',
      header: ({ table }) => {
        const isChecked = table.getIsAllRowsSelected();
        return (
          <CheckboxField
            checked={isChecked}
            indeterminate={table.getIsSomeRowsSelected() && !isChecked}
            // Applies the box's own state instead of toggling: CheckboxField
            // also fires this when `checked` changes externally, and a toggle
            // would flip that change straight back
            onChange={(nextChecked) => table.toggleAllRowsSelected(nextChecked)}
            label="Select all products"
            name="Select all products"
            labelHidden
          />
        );
      },
      cell: ({ row }) => {
        return (
          <CheckboxField
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            label={`Select product ${row.original.name}`}
            name={`Select product ${row.original.name}`}
            labelHidden
          />
        );
      },
      size: 72,
    });

    // An accessor function, since a generic row type can't resolve the 'name' key
    const nameColumn = columnHelper.accessor((row) => row.name, {
      id: 'name',
      header: (props) => (
        <SortableHeader
          // Only the noun goes through `pluralize` — its own count display
          // caps at "1,000+", and the header needs the real total
          label={`${productCount} ${pluralize({
            noun: 'product',
            number: productCount,
            returnValue: 'word',
          })}`}
          column={props.column}
        />
      ),
      enableColumnFilter: false,
      cell: ({ row }) => {
        return (
          <Flex alignItems="center" gap={16}>
            <Image
              height="48px"
              width="48px"
              border
              borderRadius="12px"
              sizes="thumbnail"
              responsive
              src={
                row.original.photos ? row.original.photos[0]?.urls.small : ''
              }
              altText={row.original.name ? row.original.name : ''}
            />

            <Typography truncateAt="275px">{row.original.name}</Typography>
          </Flex>
        );
      },
      size: 350,
    });

    const idColumn = columnHelper.accessor((row) => capitalize(row.id), {
      id: 'id',
      header: (props) => <SortableHeader label="Id" column={props.column} />,
      enableColumnFilter: false,
      size: 96,
    });

    const brandColumn = columnHelper.accessor((row) => capitalize(row.brand), {
      id: 'brand',
      header: (props) => <SortableHeader label="Brand" column={props.column} />,
      meta: {
        filterValues: filters.brand,
        filterLabel: 'Brand',
      },
    });

    const categoryColumn = columnHelper.accessor(
      (row) => capitalize(row.category),
      {
        id: 'category',
        header: (props) => (
          <SortableHeader label="Category" column={props.column} />
        ),
        meta: {
          filterValues: filters.category,
          filterLabel: 'Category',
        },
      }
    );

    const subcategoryColumn = columnHelper.accessor(
      (row) => capitalize(row.subcategory),
      {
        id: 'subcategory',
        header: (props) => (
          <SortableHeader label="Subcategory" column={props.column} />
        ),
        meta: {
          filterValues: filters.subcategory,
          filterLabel: 'Subcategory',
        },
      }
    );

    // Sorted on the low end of the range; the cell renders the whole span
    const weightColumn = columnHelper.accessor((row) => row.priceRange?.min, {
      id: 'weight',
      header: (props) => (
        <SortableHeader label="Price/Weight" column={props.column} />
      ),
      cell: ({ row }) => (
        <Typography truncateAt="100%">
          {priceWeightLabel(row.original.priceRange)}
        </Typography>
      ),
      enableColumnFilter: !unfilterable?.includes('weight'),
      meta: {
        filterValues: filters.weight,
        filterLabel: 'Weight',
        // Weights arrive as the price columns they filter on, ex `price_half_gram`
        filterOptionLabel: (value: string) => labelForVal(value) ?? value,
      },
    });

    const lineageColumn = columnHelper.accessor(
      (row) => lineageLabel(row.lineage),
      {
        id: 'lineage',
        header: (props) => (
          <SortableHeader label="Lineage" column={props.column} />
        ),
        meta: {
          filterValues: filters.lineage,
          filterLabel: 'Lineage',
          filterOptionLabel: lineageLabel,
        },
      }
    );

    // An accessor, not a display column, so the table will let it carry a filter
    const outOfStock = columnHelper.accessor((row) => row.outOfStock, {
      id: 'outOfStock',
      header: () => null,
      cell: ({ row }) =>
        row.original.outOfStock ? (
          <Popover
            target={<AlertIcon color="error" />}
            label="Out of stock"
            openOn="hover"
            alignment={{ horizontal: 'right' }}
          >
            <Popover.Content>
              <Typography>This product is out of stock</Typography>
            </Popover.Content>
          </Popover>
        ) : undefined,
      enableSorting: false,
      meta: {
        filterValues: INVENTORY_FILTER_VALUES,
        filterLabel: 'Inventory',
      },
      size: 72,
    });

    // columnHelper.columns() preserves each column's own value type; a plain
    // array widens them all and no longer satisfies the table's columns option.
    return columnHelper.columns([
      ...(selectable ? [selectColumn] : []),
      nameColumn,
      idColumn,
      brandColumn,
      categoryColumn,
      subcategoryColumn,
      weightColumn,
      lineageColumn,
      outOfStock,
    ]);
  }, [columnHelper, filters, productCount, selectable, unfilterable]);
};
